import { t } from '../../../i18next';
import SetColumnConfig from '../../../shared/components/grid/SetColumnConfig';
import { screenConfig } from '../../../shared/services/config';
import { TableRes } from '../../../shared/types';
import { normalizeDateToUTC } from '../../../shared/utils/dateUtils';
import { checkPSPTableRecordService } from '../services/checkPSPTableRecordService';
import { getTbloptNeededIdService } from '../services/getTbloptNeededIdService';
import { getTbloptStateIdService } from '../services/getTbloptStateIdService';
import HtzEditCell from './HtzEditCell';
import { getAllDropdowns } from '../../../shared/services/allDropdownsRegistry';
import { isPspSyncAppendableValue, userHasPspAccess } from '../services/pspAccessUtils';
import { isEmpty } from '../../../shared/utils';
import { PPVLeaderEditCell } from './PPVLeaderEditCell';
import ErledigtAvEditCell from './ErledigtAvEditCell';
import { isErledigtAvYesEnabled, NEED_FIELDS } from '../services/erledigtAvNeedGate';
import { getOptionValueByLabel } from '../../../shared/utils';

const columnHandlerMapping = {
  pp_erledigt: pperledigtOnChange,
  steering_state: steeringstateOnChange,
  planer: planerOnChange,
  erledigt_av: erledigtavOnChange,
  erledigt_fert: erledigtfertOnChange,
  inspection_planning_need: withNeedFieldReset('inspection_planning_need'),
  cmmvorgterm: cmmvorgtermOnChange,
  ncpneed: withNeedFieldReset('ncpneed', ncpneedOnChange),
  cmmneed: withNeedFieldReset('cmmneed'),
  qsfaneed: withNeedFieldReset('qsfaneed'),
  nc_status: ncstatusOnChange,
  cmmerl: cmmerlOnChange,
  erledigt_me: erledigtmeOnChange,
  fai_documentation_created: onChangeFaiDocCreated,
  fai_number: faiNumberOnChange,
  ppv_number: ppvNumberOnChange,
  ppv_status: ppvStatusOnChange,
};

export default function NPISteeringSetColumnConfig(props: {
  access: any;
  tableRes: TableRes;
  customProps?: any;
  userDetails: any;
  pspEnabled: boolean;
}) {
  const { access, tableRes, userDetails, pspEnabled } = props;
  const { gridColumns: columnsConfig } = SetColumnConfig(props);
  const gridColumns = columnsConfig.reduce((resp, item) => {
    const columnsWithHandlers = Object.entries(columnHandlerMapping);
    const column = columnsWithHandlers.find(([colName]) => colName === item.attrInternalName);
    if (column) {
      const [, handlerFn] = column;
      const { allAccessibleColumns } = item;
      item.onValueChange = (params) => {
        return handlerFn({ ...params, ...props.customProps, allAccessibleColumns });
      };
    }
    if (item.attrInternalName === 'mp_nr') {
      item.creatable = true;
      item.valueGetter = (value) => {
        return value;
      };
      item.valueFormatter = (value) => {
        return value;
      };
      if (access?.on_change_of_mp_nr) {
        item.onValueChange = (params) =>
          mpnrOnChange({ ...params, ...props.customProps, userDetails, access, pspEnabled });
      }
    }
    if (item.attrInternalName === 'htz') {
      item.renderEditCell = (params) => <HtzEditCell {...params} tableRes={tableRes} />;
    }

    if (item.attrInternalName === 'ppv_leader') {
      item.renderEditCell = (params) => <PPVLeaderEditCell {...params} />;
    }

    if (item.attrInternalName === 'erledigt_av') {
      item.renderEditCell = (params) => <ErledigtAvEditCell {...params} />;
    }
    resp.push(item);
    return resp;
  }, []);
  return { gridColumns };
}

function withNeedFieldReset(field: (typeof NEED_FIELDS)[number], handlerFn?: (params: any) => any) {
  return (params: any) => {
    const result = handlerFn ? handlerFn(params) : undefined;
    queueResetWorkPrepIfNeedMakesItInvalid({
      field,
      params,
      doneStateYesId: params?.doneStateYesId,
    });
    return result;
  };
}

function queueResetWorkPrepIfNeedMakesItInvalid({ field, params, doneStateYesId }) {
  if (!NEED_FIELDS.includes(field as (typeof NEED_FIELDS)[number])) {
    return;
  }

  const { row, rowId, value, updateRow } = params ?? {};
  const allDropdowns = getAllDropdowns();
  const resolvedYesId = getOptionValueByLabel(allDropdowns?.erledigt_av, 'yes', doneStateYesId);
  const resolvedInWorkId = getOptionValueByLabel(allDropdowns?.erledigt_av, 'in work', null);

  if (!updateRow || resolvedYesId == null || String(row?.erledigt_av) !== String(resolvedYesId)) {
    return;
  }

  const nextRow = {
    ...row,
    [field]: value,
  };

  if (isErledigtAvYesEnabled({ row: nextRow, allDropdowns })) {
    return;
  }

  updateRow({
    rowId,
    updatedFields: {
      erledigt_av: resolvedInWorkId,
      avcomp: null,
    },
  });
}

export function pperledigtOnChange(props) {
  const { rowId, value, updateRow, doneStateYesId } = props;
  const allDropdowns = getAllDropdowns();
  const doneStateIdFromDropdown = allDropdowns?.pp_erledigt?.find(
    (item) =>
      String(item?.label ?? '')
        .trim()
        .toLowerCase() === 'done',
  )?.value;

  const resolvedDoneStateId = doneStateIdFromDropdown ?? doneStateYesId;
  const isDone =
    resolvedDoneStateId !== null && value !== null && String(value) === String(resolvedDoneStateId);

  const updatedFields = {
    pp_erledigt_datum: null,
  };

  if (isDone) {
    const timestamp = new Date().toISOString().substring(0, 19);
    updatedFields.pp_erledigt_datum = timestamp;
  }

  updateRow({ rowId, updatedFields });
}

function steeringstateOnChange(props) {
  const { value, setAlertbar } = props;
  if (value != 'NPII' && value != 'NPIO' && value != null) {
    setAlertbar({
      children: t('npiSteering.steeringStateAlert'),
      severity: 'error',
      stopAutoClose: true,
    });
    return false;
  }
}

const mpnrOnChange = async (props) => {
  const { rowId, value, row, updateRow, setAlertbar, screenGridId, pspEnabled } = props;

  const allDropdowns = getAllDropdowns();
  const augId = allDropdowns?.werk?.find((item) => item.label === 'AUG')?.value ?? '1100';
  const isPlantAugsburg = row?.werk === augId;
  const hasValue = value !== null && value !== undefined;
  const hasRequiredSyncFields =
    hasValue &&
    row?.pf_lc !== null &&
    row?.pf_lc !== undefined &&
    row?.design_owner_id !== null &&
    row?.design_owner_id !== undefined;

  if (!pspEnabled) {
    return;
  }

  // Check if the plant is Augsburg and MP Nr. has a value, as these are prerequisites for PSP sync
  if (!isPlantAugsburg || !value) {
    return;
  }

  // Check if user has necessary role access and required PSP key fields are present before allowing sync
  const canEdit = await userHasPspAccess(props.userDetails.roleIds, row, screenGridId);
  if (!canEdit) {
    return;
  }

  // If required fields for PSP sync are not present, show warning and do not proceed with sync
  if (!hasRequiredSyncFields) {
    setAlertbar({
      children: t('npiSteering.psp_sync_not_possible'),
      severity: 'warning',
    });
    return;
  }

  const mpNrWhere = {
    items: [
      {
        field: 'mp_nr',
        id: 1,
        operator: 'equals',
        value: value,
      },
      {
        field: 'pf_lc',
        id: 2,
        operator: 'is',
        value: row?.pf_lc,
      },
      {
        field: 'design_owner_id',
        id: 3,
        operator: 'is',
        value: row?.design_owner_id,
      },
      {
        field: 'active',
        operator: 'is',
        value: 1,
      },
    ],
    logicOperator: 'and',
  };
  // Fetch Active PSP Key matching and other fields for sync
  const resp = await checkPSPTableRecordService({ mpNrWhere, screenGridId });
  const [pspTableResp] = resp?.npi_psptable ?? [];
  if (!pspTableResp) {
    setAlertbar({
      children: t('npiSteering.psp_sync_missing_active_data'),
      severity: 'warning',
    });
    return;
  }

  // Map values fetched from PSP key screen data to NPI Steering target fields.
  // Only append to NPI Steering when the current field is empty or a placeholder.
  const fetchedPspValuesByNpiSteeringField = {
    capex_id: pspTableResp?.capex_id,
    poe: pspTableResp?.poe,
    anzustrebender_liefertermin: pspTableResp?.anzustrebender_liefertermin,
    psp_c_a_element_cmm: pspTableResp?.psp_c_a_element_cmm,
    psp_c_a_element_ncp: pspTableResp?.psp_c_a_element_ncp,
    psp_c_a_element_femi: pspTableResp?.psp_c_a_element_femi,
    capex_stunden: pspTableResp?.budget_ncp,
    cmmhrs: pspTableResp?.budget_cmm,
    hardtooling_euro: pspTableResp?.budget_femi,
  };

  const updatedFields = Object.entries(fetchedPspValuesByNpiSteeringField).reduce(
    (acc, [field, sourceValue]) => {
      const hasSourceValue = !isEmpty(sourceValue);
      if (isPspSyncAppendableValue(row?.[field]) && hasSourceValue) {
        acc[field] = sourceValue;
      }
      return acc;
    },
    {},
  );

  if (Object.keys(updatedFields).length) {
    const status = row?.status === 'N' || row?.status === 'C' ? row.status : 'E';
    updateRow({ rowId, updatedFields: { ...updatedFields, status } });
    setAlertbar({
      children: t('npiSteering.psp_sync_success'),
      severity: 'success',
    });
  }
};

function erledigtavOnChange(props) {
  const { rowId, value, updateRow, doneStateYesId } = props;
  const updatedFields = { avcomp: null };
  if (value === doneStateYesId) {
    const timestamp = new Date().toISOString().substring(0, 19);
    updatedFields.avcomp = timestamp;
  }
  updateRow({ rowId, updatedFields });
}

export async function planerOnChange(props) {
  const { rowId, value, row, updateRow, screenGridId } = props;

  if (value == null || value === '' || screenGridId == null) {
    return;
  }

  const stateRespAssigned = await getTbloptStateIdService({
    state: 'Assigned',
    screenGridId,
  });
  const [assignedState] = stateRespAssigned?.npi_tblopt_done_state ?? [];
  const assignedStateId = assignedState?.id;

  if (!assignedStateId || row?.erledigt_av === assignedStateId) {
    return;
  }

  updateRow({
    rowId,
    updatedFields: {
      erledigt_av: assignedStateId,
    },
  });
}

function erledigtfertOnChange(props) {
  const { rowId, value, updateRow, doneStateYesId } = props;
  const updatedFields = { fertcomp: null };
  if (value === doneStateYesId) {
    const timestamp = new Date().toISOString().substring(0, 19);
    updatedFields.fertcomp = timestamp;
  }
  updateRow({ rowId, updatedFields });
}

async function cmmvorgtermOnChange(props) {
  const { rowId, value, updateRow, screenGridId } = props;
  if (value) {
    const resp = await getTbloptNeededIdService({ need: 'Benötigt', screenGridId });
    const resp2 = await getTbloptStateIdService({ state: 'New', screenGridId });
    const [needId] = resp?.npi_tblopt_needed ?? [];
    const [stateId] = resp2?.npi_tblopt_done_state ?? [];
    if (needId && stateId) {
      const updatedFields = { cmmneed: needId.id, cmmerl: stateId.id };
      updateRow({ rowId, updatedFields });
    }
  }
}

async function ncpneedOnChange(props) {
  const { rowId, value, updateRow, doneStateNoId } = props;
  if (value === 1) {
    const updatedFields = { ncperl: doneStateNoId };
    updateRow({ rowId, updatedFields });
  }
}

async function ncstatusOnChange(props) {
  const { rowId, value, updateRow, doneStateYesId, doneStateNoId } = props;
  if (value == 3) {
    const timestamp = new Date().toISOString().substring(0, 19);
    const updatedFields = { ncperl: doneStateYesId, ncpfertigam: timestamp };
    updateRow({ rowId, updatedFields });
  } else {
    const updatedFields = { ncperl: doneStateNoId, ncpfertigam: null };
    updateRow({ rowId, updatedFields });
  }
}

async function cmmerlOnChange(props) {
  const { rowId, value, updateRow, screenGridId } = props;
  const updatedFields = { hw_tooling_lt: null };
  if (value) {
    const resp = await getTbloptNeededIdService({ need: 'Benötigt', screenGridId });
    const [needId] = resp?.npi_tblopt_needed ?? [];
    if (value === needId.id) {
      const timestamp = new Date().toISOString().substring(0, 19);
      updatedFields.hw_tooling_lt = timestamp;
    }
  }
  updateRow({ rowId, updatedFields });
}

export function erledigtmeOnChange(props) {
  const {
    rowId,
    value,
    row,
    updateRow,
    setAlertbar,
    language,
    doneStateNoId,
    doneStateYesId,
    boolNoId,
    allAccessibleColumns,
  } = props;
  if (value === doneStateYesId) {
    const columnsList = screenConfig?.['npi_steering']?.onChange_erledigt_me ?? [];
    const columnsDetails = allAccessibleColumns.filter(
      (item) => item.attrGroupId === 6 && columnsList.includes(item.attrInternalName),
    );
    const missingFields = getMissingFields({
      row,
      language,
      columnsDetails,
    });
    if (missingFields?.length) {
      const msg = `${t('npiSteering.erledigtmeAlert')}${missingFields.join(', ')}`;
      setAlertbar({ children: msg, severity: 'error', stopAutoClose: true });
      return false;
    }
    const timestamp = new Date().toISOString().substring(0, 19);
    let updatedFields: unknown = { htz_an_av_plm: timestamp };
    if (row.pf_lc === 281 || row.pf_lc === 280) {
      updatedFields = {
        htz_an_av_plm: timestamp,
        erledigt_av: doneStateYesId,
        erledigt_fert: doneStateYesId,
        avcomp: timestamp,
        fertcomp: timestamp,
      };
    }
    updateRow({ rowId, updatedFields });
  }

  if (value === doneStateNoId && row.erledigt_av === doneStateNoId) {
    const updatedFields = {
      htz_an_av_plm: null,
      erledigt_fert: doneStateNoId,
      ncperl: doneStateNoId,
      cmmerl: doneStateNoId,
      qsfaerl: doneStateNoId,
      dispo_sicht_angelegt: boolNoId,
    };
    updateRow({ rowId, updatedFields });
  } else if (value === doneStateNoId && (row.pf_lc === 281 || row.pf_lc === 280)) {
    const updatedFields = {
      htz_an_av_plm: null,
      erledigt_av: doneStateNoId,
      erledigt_fert: doneStateNoId,
      ncperl: doneStateNoId,
      cmmerl: doneStateNoId,
      qsfaerl: doneStateNoId,
      dispo_sicht_angelegt: boolNoId,
      avcomp: null,
      fertcomp: null,
    };
    updateRow({ rowId, updatedFields });
  } else if (value === doneStateNoId) {
    const updatedFields = {
      htz_an_av_plm: null,
      erledigt_av: doneStateNoId,
      avcomp: null,
    };
    updateRow({ rowId, updatedFields });
  }
}

export function getMissingFields(props) {
  const { row, language, columnsDetails } = props;
  const missingFields = columnsDetails.reduce((resp, item) => {
    if (!row[item.attrInternalName]) {
      resp.push(item[`attrDisplayName${language}`]);
    }
    return resp;
  }, []);
  return missingFields;
}

function onChangeFaiDocCreated(props) {
  const { rowId, value, updateRow, doneStateYesId } = props;
  updateDateIfDone({
    field: 'fai_documentation_create_date',
    value,
    yesValue: doneStateYesId,
    rowId,
    updateRow,
  });
}

function updateDateIfDone({ field, value, yesValue, rowId, updateRow }) {
  const updatedFields = {};
  updatedFields[field] = null;
  if (value === yesValue) {
    const timestamp = new Date().toISOString().substring(0, 19);
    updatedFields[field] = timestamp;
  }
  updateRow({ rowId, updatedFields });
}

// PPV status IDs are defined by npi.tblopt_ppv_status
// 1 = Launched
// 2 = Temporary with open actions
// 3 = Completed
// 4 = Cancelled
function faiNumberOnChange(props) {
  const { rowId, row, updateRow, value } = props;

  const hasFaiNumber = value !== null && value !== undefined && value !== '';
  const ppvStatusIsEmpty =
    row?.ppv_status === null || row?.ppv_status === undefined || row?.ppv_status === '';

  const ppv_number = `${value}PPV`;
  // Change PPV Status
  if (hasFaiNumber && ppvStatusIsEmpty) {
    const allDropdowns = getAllDropdowns();
    const PPV_STATUS_LAUNCHED_ID =
      allDropdowns?.ppv_status?.find((item) => item.label === 'Launched')?.value ?? 1;
    // PPV Status to "Launched"
    updateRow({ rowId, updatedFields: { ppv_status: PPV_STATUS_LAUNCHED_ID } });
  }
  // Change PPV Number
  if (hasFaiNumber) updateRow({ rowId, updatedFields: { ppv_number } });
  else updateRow({ rowId, updatedFields: { ppv_number: '' } });
}

function ppvNumberOnChange({ rowId, updateRow, value, row }) {
  const val = String(value || '').trim();
  const oldVal = row?.ppv_number || null;
  // Handle Empty Logic
  if (val === '') {
    const ppv_number = oldVal ? (oldVal === 'emptyPPV' ? '' : 'emptyPPV') : null;
    updateRow({ rowId, updatedFields: { ppv_number } });
    return ppv_number;
  }

  // Handle Non-empty, ends with PPV logic
  const cleanValue = val.replace(/PPV$/i, '');
  const ppv_number = `${cleanValue}PPV`;

  updateRow({ rowId, updatedFields: { ppv_number } });
  return ppv_number;
}

// PPV status IDs are defined by npi.tblopt_ppv_status and inserted in this order:
// 1 = Launched
// 2 = Temporary with open actions
// 3 = Completed
// 4 = Cancelled
function ppvStatusOnChange(props) {
  const { rowId, value, row, updateRow } = props;
  const allDropdowns = getAllDropdowns();
  const PPV_STATUS_COMPLETED_ID =
    allDropdowns?.ppv_status?.find((item) => item.label === 'Completed')?.value ?? 3;

  const wasCompleted = row?.ppv_status == PPV_STATUS_COMPLETED_ID;
  const isCompleted = value == PPV_STATUS_COMPLETED_ID;

  if (isCompleted) {
    const today = normalizeDateToUTC(new Date());
    // PPV Completed to Today's Date
    updateRow({ rowId, updatedFields: { ppv_completed_date: today } });
    return;
  }
  if (wasCompleted && !isCompleted) {
    updateRow({ rowId, updatedFields: { ppv_completed_date: null } });
  }
}
